/**
 * Phase 1 shared fixture (MathToolImplementation.md §9.1).
 *
 * A minimal but real inquiry, expressed as a fixed seed event log so every agent
 * tests against identical ground truth. This file ships with the contracts so
 * Agents 2–7 import the same data.
 *
 * The slice (§9.1):
 *   - Constraints (all adopted-formal): `logic`, `light-bends`, `parallel-postulate`.
 *   - An external constraint, importable as a `constraint` edge: `ext-observation`.
 *   - Claims:
 *       `scalar-gravity`  violates `light-bends`        (refuted in the default config)
 *       `non-euclidean`   violates `parallel-postulate` (refuted in the default config)
 *       `substrate-sr`    profile ⟨W1, interp, M0⟩      (explorable in the default config)
 *
 * The four expected behaviours (mirroring the proved EngineV2.Demo: false/true/true/false):
 *   1. `scalar-gravity` is refuted in the default config.
 *   2. challenging `light-bends` makes `scalar-gravity` explorable.
 *   3. importing a `resource`/`heuristic` edge changes no verdict (edge inertness).
 *   4. importing the `ext-observation` `constraint` edge refutes the previously-explorable
 *      `substrate-sr`.
 *
 * DESIGN NOTE (logged in DECISIONS_Agent1.md, D1): §9.1 says `substrate-sr`
 * "violates nothing" AND that importing `ext-observation` "refutes a previously-
 * explorable claim". The only claim explorable in the default config is
 * `substrate-sr` (the other two violate active adopted-formal constraints). To
 * reproduce behaviour #4 faithfully — exactly as the Lean demo's claim 5 violates
 * external constraint 3 — `substrate-sr.violates` lists ONLY `ext-observation`,
 * which is NOT in the default adopted-formal base. So `substrate-sr` violates
 * nothing among the *adopted base* (hence explorable by default) yet is refuted
 * once the external constraint edge is composed in.
 */
import { claimId, constraintId, edgeId, messageId } from '../ids.js';
// --- ids ---
export const C = {
    logic: constraintId('logic'),
    lightBends: constraintId('light-bends'),
    parallelPostulate: constraintId('parallel-postulate'),
    /** External — NOT a domain constraint; importable via a `constraint` edge. */
    extObservation: constraintId('ext-observation'),
};
export const CL = {
    scalarGravity: claimId('scalar-gravity'),
    nonEuclidean: claimId('non-euclidean'),
    substrateSr: claimId('substrate-sr'),
};
// --- profiles (only substrate-sr's is fixed by §9.1; the others are illustrative) ---
export const PROFILES = {
    [CL.scalarGravity]: { w: 2, e: 'refuted', m: 1 },
    [CL.nonEuclidean]: { w: 1, e: 'tested', m: 0 },
    [CL.substrateSr]: { w: 1, e: 'interp', m: 0 },
};
// --- claims ---
export const CLAIMS = {
    [CL.scalarGravity]: {
        id: CL.scalarGravity,
        labelId: messageId('claim.scalar-gravity'),
        violates: [C.lightBends],
        needs: [C.logic],
        profile: PROFILES[CL.scalarGravity],
    },
    [CL.nonEuclidean]: {
        id: CL.nonEuclidean,
        labelId: messageId('claim.non-euclidean'),
        violates: [C.parallelPostulate],
        needs: [C.logic],
        profile: PROFILES[CL.nonEuclidean],
    },
    [CL.substrateSr]: {
        id: CL.substrateSr,
        labelId: messageId('claim.substrate-sr'),
        violates: [C.extObservation],
        needs: [C.logic],
        profile: PROFILES[CL.substrateSr],
    },
};
// --- the importable external constraint edge (composes ext-observation) ---
export const EXT_OBSERVATION_EDGE = {
    id: edgeId('edge-ext-observation'),
    kind: 'constraint',
    externalConstraintId: C.extObservation,
    sourceLabelId: messageId('edge.ext-observation'),
};
// --- inert edges (demonstrate behaviour #3: importing these changes no verdict) ---
export const RESOURCE_EDGE = {
    id: edgeId('edge-resource-sample'),
    kind: 'resource',
    sourceLabelId: messageId('edge.resource-sample'),
};
export const HEURISTIC_EDGE = {
    id: edgeId('edge-heuristic-sample'),
    kind: 'heuristic',
    sourceLabelId: messageId('edge.heuristic-sample'),
};
export const SHARED_FOUNDATION_EDGE = {
    id: edgeId('edge-shared-sample'),
    kind: 'shared-foundation',
    sourceLabelId: messageId('edge.shared-sample'),
};
// --- the fixed seed event log (replay this to reach the Phase 1 base state) ---
export const SEED_LOG = [
    { t: 'constraint-declared', id: C.logic, status: 'adopted-formal', labelId: messageId('constraint.logic') },
    {
        t: 'constraint-declared',
        id: C.lightBends,
        status: 'adopted-formal',
        labelId: messageId('constraint.light-bends'),
    },
    {
        t: 'constraint-declared',
        id: C.parallelPostulate,
        status: 'adopted-formal',
        labelId: messageId('constraint.parallel-postulate'),
    },
    { t: 'claim-added', claim: CLAIMS[CL.scalarGravity] },
    { t: 'claim-added', claim: CLAIMS[CL.nonEuclidean] },
    { t: 'claim-added', claim: CLAIMS[CL.substrateSr] },
];
/** The default active set after replaying SEED_LOG: exactly the adopted-formal ids. */
export const DEFAULT_ACTIVE = [C.logic, C.lightBends, C.parallelPostulate];
/**
 * The four ground-truth demo expectations (EngineV2.Demo → false/true/true/false),
 * exported as data so the kernel suite and Agent 2's conformance harness check the
 * identical outcomes. `refuted` here is the negation of the Lean `explorable` #eval.
 */
export const DEMO_EXPECTATIONS = {
    /** #1 explorable actualWorld scalar = false ⇒ refuted. */
    scalarGravityRefutedByDefault: true,
    /** #2 explorable (challenge light-bends) scalar = true ⇒ not refuted. */
    scalarGravityExplorableWhenLightBendsChallenged: true,
    /** #3 inert edges leave verdicts unchanged. */
    inertEdgesChangeNoVerdict: true,
    /** #4 explorable (import ext-observation) substrate-sr = false ⇒ refuted. */
    substrateSrRefutedAfterExternalImport: true,
};
//# sourceMappingURL=index.js.map